import React, { useState, useEffect } from 'react';
import './SubjectList.css';
import { getAllQuestionBanks, refreshAllData } from '../utils/generateQuestionBanks';

function SubjectList({ onSelectSubject }) {
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);

  const fetchSubjects = async (forceRefresh = false) => {
    setLoading(true);
    try {
      const data = await getAllQuestionBanks(forceRefresh);
      
      if (data.subjects) {
        const subjectList = Object.keys(data.subjects).map(key => ({
          id: key,
          name: data.subjects[key].name || key,
          banksCount: data.subjects[key].banks ? data.subjects[key].banks.length : 0
        }));
        setSubjects(subjectList);
        
        if (forceRefresh) {
          setLastRefreshTime(new Date());
        }
      } else {
        setSubjects([]);
      }
    } catch (error) {
      console.error('获取学科失败:', error);
      setSubjects([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // 页面加载时总是强制刷新，确保获取最新数据
    const forceRefresh = true;
    fetchSubjects(forceRefresh);
    localStorage.setItem('lastQuestionBankRefresh', new Date().toISOString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 监听页面重新显示，检查是否需要刷新
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // 页面重新显示时，检查是否需要刷新
        const shouldAutoRefresh = () => {
          const lastRefresh = localStorage.getItem('lastQuestionBankRefresh');
          if (!lastRefresh) return true;
          
          const lastRefreshTime = new Date(lastRefresh);
          const now = new Date();
          const timeDiff = now - lastRefreshTime;
          
          // 如果超过3分钟，自动刷新
          return timeDiff > 3 * 60 * 1000;
        };

        if (shouldAutoRefresh()) {
          console.log('🔄 检测到数据可能过期，自动刷新...');
          fetchSubjects(true);
          localStorage.setItem('lastQuestionBankRefresh', new Date().toISOString());
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshAllData();
      await fetchSubjects(true);
      localStorage.setItem('lastQuestionBankRefresh', new Date().toISOString());
      
      // 显示刷新成功提示
      if (window.antd && window.antd.message) {
        window.antd.message.success('题库数据已刷新');
      } else {
        console.log('✅ 题库数据已刷新');
      }
    } catch (error) {
      console.error('刷新失败:', error);
      if (window.antd && window.antd.message) {
        window.antd.message.error('刷新失败，请重试');
      }
    } finally {
      setRefreshing(false);
    }
  };

  const handleSubjectClick = (subject) => {
    if (onSelectSubject) {
      onSelectSubject(subject.id, subject.name);
    }
  };

  if (loading) {
    return <div className="loading-container">加载学科中...</div>;
  }

  if (subjects.length === 0) {
    return (
      <div className="no-subjects">
        <p>暂无学科数据</p>
        <p className="sub-text">请确保题库已正确导入</p>
        <button 
          className="refresh-button"
          onClick={handleRefresh}
          disabled={refreshing}
        >
          {refreshing ? '刷新中...' : '刷新题库'}
        </button>
      </div>
    );
  }

  return (
    <div className="subject-list">
      <div className="subject-header">
        <h2>选择学科</h2>
        <div className="header-actions">
          {lastRefreshTime && (
            <span className="last-refresh">
              上次刷新: {lastRefreshTime.toLocaleTimeString()}
            </span>
          )}
          <button 
            className="refresh-button"
            onClick={handleRefresh}
            disabled={refreshing}
            title="刷新题库数据"
          >
            {refreshing ? '🔄' : '🔄'} {refreshing ? '刷新中...' : '刷新'}
          </button>
        </div>
      </div>
      
      <div className="subjects-container">
        {subjects.map((subject) => (
          <div
            key={subject.id}
            className="subject-card"
            onClick={() => handleSubjectClick(subject)}
          >
            <h3>{subject.name}</h3>
            <div className="subject-info">
              <span>题库数量: {subject.banksCount}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default SubjectList;